import React, { useEffect, useState } from "react";
import styled from "styled-components";
import OtpDigitInput from "./OtpDigitInput";
import {
    ExternalTitle,
    ExternalSubtitle,
    ExternalLink,
    ExternalErrorMessage,
    PrimaryButton,
} from "./styled.js";
import { EXTERNAL_ACCESS_COPY, RESEND_COOLDOWN_SECONDS } from "./constants";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 400px;
  max-width: 100%;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 19px;
  color: #404040;
`;

const CodeBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorRow = styled(ExternalErrorMessage)`
  margin-top: 6px;
`;

const ResendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: ${(props) => (props.$hasError ? "20px" : "8px")};
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #64758b;
`;

const SubmitButton = styled(PrimaryButton)`
  margin-top: 20px;
`;

const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6h16v12H4z" stroke="#404040" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="m4 7 8 6 8-6" stroke="#404040" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);

export default function ExternalOtpStep({
    email,
    otp,
    onOtpChange,
    onSubmit,
    onResend,
    onChangeEmail,
    error,
    isLoading,
    cooldownStartedAt,
    resetKey,
}) {
    const [secondsLeft, setSecondsLeft] = useState(0);

    useEffect(() => {
        if (!cooldownStartedAt) return undefined;
        const tick = () => {
            const elapsed = Math.floor((Date.now() - cooldownStartedAt) / 1000);
            setSecondsLeft(Math.max(RESEND_COOLDOWN_SECONDS - elapsed, 0));
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [cooldownStartedAt]);

    return (
        <Wrapper>
            <TitleBlock>
                <HeadingGroup>
                    <ExternalTitle>{EXTERNAL_ACCESS_COPY.otpTitle}</ExternalTitle>
                    <ExternalSubtitle>{EXTERNAL_ACCESS_COPY.otpSubtitle}</ExternalSubtitle>
                </HeadingGroup>
                <EmailRow>
                    <MailIcon />
                    <span>{email}</span>
                    <ExternalLink type="button" onClick={onChangeEmail} disabled={isLoading}>
                        {EXTERNAL_ACCESS_COPY.changeEmail}
                    </ExternalLink>
                </EmailRow>
            </TitleBlock>

            <CodeBlock>
                <OtpDigitInput
                    value={otp}
                    onChange={onOtpChange}
                    hasError={Boolean(error)}
                    onSubmit={onSubmit}
                    resetKey={resetKey}
                />

                {error && <ErrorRow>{error}</ErrorRow>}

                <ResendRow $hasError={Boolean(error)}>
                    <span>{EXTERNAL_ACCESS_COPY.resendPrompt}</span>
                    <ExternalLink
                        type="button"
                        onClick={onResend}
                        disabled={secondsLeft > 0 || isLoading}
                    >
                        {secondsLeft > 0
                            ? EXTERNAL_ACCESS_COPY.resendCountdown.replace('{{seconds}}', secondsLeft)
                            : EXTERNAL_ACCESS_COPY.resend}
                    </ExternalLink>
                </ResendRow>

                <SubmitButton disabled={otp.length < 6 || isLoading} onClick={onSubmit}>
                    {isLoading ? 'Verifying…' : 'Continue'}
                </SubmitButton>
            </CodeBlock>
        </Wrapper>
    );
}
